import React from 'react';
import MessageCourier from '../../../lib/MessageCourier';
import SystemClient from '../../../system/SystemClient';
import { PlanillaPartido } from '../../../system/PlanillaPartido/PlanillaPartido';
import PlanillaDigitalForm from '../PlanillaDigitalForm';
import DialogTemplate from '../../Common/DialogTemplate';
import { Icon, Label, TextField, ComboBox, Text } from '@fluentui/react';
import useAsyncDataCourier from '../../../common/custom-hooks/useAsyncDataCourier';
import { Jugador } from '../../../system/Jugador/Jugador';
import { JugadorSelector } from '../../Jugador/JugadorSelector';
import { PlanillaPartidoJugador } from '../../../system/PlanillaPartidoJugador/PlanillaPartidoJugador';
import { createArtificialId } from '../../../common/utils';
import JugadorOfflineForm from '../../PlanillaPartidoOffline/JugadorOfflineForm';
import InputField from '../../../lib/FormValidator/Form/InputField';
import useForceUpdate from '../../../common/custom-hooks/useForceUpdate';
import { useCustomInput } from '../../../common/custom-hooks/useCustomInput';
import ComboBoxInput from '../../../lib/FormValidator/Form/ComboBoxInput';
import { tipoDocumentoOptions } from '../../PlanillaPartidoOffline/ConfeccionPlanillaPartidoTeamOffline';

interface IAgregarJugadorDialog {
  form: PlanillaDigitalForm;
  system: SystemClient;
  courier: MessageCourier;
  changeBlockUI: (state: boolean) => void;
  planillaPartido: PlanillaPartido;
  isPartidoOnline: boolean;
  showAgregarJugador: boolean;
  setShowAgregarJugador: (v: boolean) => void;
  setShowAgregarJugadorTeam: (v: string) => void;
  showAgregarJugadorTeam: string;
}

const AgregarJugadorDialog = ({
  form,
  system,
  courier,
  changeBlockUI,
  planillaPartido,
  isPartidoOnline,
  showAgregarJugador,
  setShowAgregarJugador,
  setShowAgregarJugadorTeam,
  showAgregarJugadorTeam,
}: IAgregarJugadorDialog) => {
  const [contextListJugador, setContextListJugador] = React.useState<Jugador[]>([]);
  const [jugadorAAgregar, setJugadorAAgregar] = React.useState<Jugador | undefined>(undefined);
  const [dorsal, setDorsal] = React.useState<number>(0);
  const [dorsalEsValido, setDorsalEsValido] = React.useState<boolean>(false);
  const [showErrors, setShowErrors] = React.useState<boolean>(false);

  const equipoLocalId = planillaPartido.getPartido().getEqidLocal();
  const equipoVisitanteId = planillaPartido.getPartido().getEqidVisitante();

  const context = useAsyncDataCourier(
    () =>
      isPartidoOnline
        ? system.getPlanillaPartidoSystem().getContextForConfeccion(planillaPartido.getPartido())
        : Promise.resolve(undefined),
    [system],
    courier
  );

  React.useEffect(() => {
    if (context) {
      if (showAgregarJugadorTeam === 'L') {
        if (planillaPartido.getPartido().getTorneo()?.getTieneListaBuenaFe()) {
          setContextListJugador(context.jugadoresListaBuenaFeLocal.map((j) => j.getJugador()!));
        } else {
          setContextListJugador(context.jugadoresPosiblesEquipoLocal);
        }
      } else if (showAgregarJugadorTeam === 'V') {
        if (planillaPartido.getPartido().getTorneo()?.getTieneListaBuenaFe()) {
          setContextListJugador(context.jugadoresListaBuenaFeVisitante.map((j) => j.getJugador()!));
        } else {
          setContextListJugador(context.jugadoresPosiblesEquipoVisitante);
        }
      }
    }
  }, [context, showAgregarJugadorTeam]);

  const filterListContext = () => {
    const _list = contextListJugador.filter((j) => {
      let existe = false;
      planillaPartido.getPlanillaPartidoJugadorList().forEach((ppj) => {
        if (j.isIdentifiedAs(ppj.getJugador())) {
          existe = true;
        }
      });
      let ret = !existe;
      return ret;
    });
    return _list.sort((a, b) => Jugador.sortByFullameFn(a, b));
  };

  const addJugador = async (j: Jugador) => {
    changeBlockUI(true);
    try {
      if (isPartidoOnline) {
        const ppj = PlanillaPartidoJugador.createFrom(
          '',
          +planillaPartido.getId()!,
          showAgregarJugadorTeam === 'L' ? equipoLocalId! : equipoVisitanteId!,
          +j.getId()!,
          dorsal,
          false,
          j,
          false
        );

        const savedPpj = await system
          .getPlanillaPartidoJugadorSystem()
          .saveFromPlanilla(ppj, String(planillaPartido.getPartido().getTorid()));

        planillaPartido.addPlanillaPartidoJugadorToList(savedPpj);
      } else {
        const ppj = PlanillaPartidoJugador.createFrom(
          createArtificialId().toString(),
          +planillaPartido.getId()!,
          showAgregarJugadorTeam === 'L' ? equipoLocalId! : equipoVisitanteId!,
          +j.getId()!,
          dorsal,
          false,
          j,
          false
        );
        planillaPartido.addPlanillaPartidoJugadorToList(ppj);
      }
      form.getPlanillaPartidoInput().setValue(planillaPartido);
    } catch (error) {
      courier.messageError(error);
    } finally {
      setJugadorAAgregar(undefined);
      changeBlockUI(false);
      setShowAgregarJugador(false);
      setDorsal(0);
    }
  };

  const dorsalValido = (dorsal: number) => {
    let valid = true;
    if (dorsal < 1 || dorsal > 99) {
      valid = false;
    }
    const eqId = showAgregarJugadorTeam === 'L' ? equipoLocalId! : equipoVisitanteId!;
    planillaPartido.getPlanillaPartidoJugadorList().forEach((ppj) => {
      if (ppj.getDorsal() == dorsal && ppj.getEqid() == eqId) {
        valid = false;
      }
    });
    setDorsalEsValido(valid);
  };

  const yaEstaElDocumento = () => {
    let ret = false;
    planillaPartido.getPlanillaPartidoJugadorList().forEach((ppj) => {
      if (
        ppj.getJugador().getDocumentoNumero() === nroDocInput.getValue() &&
        +ppj.getJugador().getDtiid() == +tipoDocInput.getValue()!
      ) {
        ret = true;
      }
    });
    return ret;
  };

  const resetForm = () => {
    setShowErrors(false);
    setJugadorAAgregar(undefined);
  };

  const teamName =
    showAgregarJugadorTeam === 'L'
      ? form.getEquipoLocal().getClub()?.getNombre()
      : form.getEquipoVisitante().getClub()?.getNombre();

  //--------------- --------------- --------------- --------------- ---------------  NUEVO

  const [jugadorOfflineForm, setJugadorOfflineForm] = React.useState<JugadorOfflineForm>(
    JugadorOfflineForm.initialize()
  );

  const [formExtraIsValid, setFormExtraIsValid] = React.useState(false);
  const forceUpdate = useForceUpdate();

  const onBlurExtraCustomInput = () => {
    if (jugadorOfflineForm)
      setFormExtraIsValid(
        jugadorOfflineForm.getIdInput().getValue()
          ? !jugadorOfflineForm.hasErrors()
          : jugadorOfflineForm.isValid()
      );
    forceUpdate();
  };

  const nombreInput = useCustomInput(() => jugadorOfflineForm.getNombreInput(), [form]);
  const apellidoInput = useCustomInput(() => jugadorOfflineForm.getApellidoInput(), [form]);
  const nroDocInput = useCustomInput(() => jugadorOfflineForm.getNroDocInput(), [form]);
  const tipoDocInput = useCustomInput(() => jugadorOfflineForm.getTipoDocInput(), [form]);

  const validateAceptar = () => {
    // deshabbilita el boton de aceptar
    if (!isPartidoOnline) {
      return !dorsalEsValido || !formExtraIsValid || yaEstaElDocumento();
    } else {
      return !dorsalEsValido || !jugadorAAgregar;
    }
  };
  return (
    <>
      <DialogTemplate
        showDialog={showAgregarJugador}
        toggleShowDialog={() => {
          resetForm();
          setShowAgregarJugador(false);
          setShowAgregarJugadorTeam('');
        }}
        title={'Agregar jugador en ' + teamName}
        validateAccept={() => validateAceptar()}
        onAccept={() => {
          if (!isPartidoOnline) {
            const jug = Jugador.fromOffline(
              createArtificialId().toString(),
              nombreInput.getValue(),
              apellidoInput.getValue(),
              nroDocInput.getValue(),
              tipoDocInput.getValue()
            );
            setJugadorAAgregar(jug);
            addJugador(jug);
            setJugadorOfflineForm(JugadorOfflineForm.initialize());
          } else {
            if (jugadorAAgregar) addJugador(jugadorAAgregar);
          }
        }}
        onCancel={() => {
          setShowAgregarJugador(false);
          setShowAgregarJugadorTeam('');
          resetForm();
          setJugadorOfflineForm(JugadorOfflineForm.initialize());
        }}
        acceptText={'Agregar'}
        cancelText={'Volver'}
        minWidth={800}
      >
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm2">
              <TextField
                value={!!dorsal ? dorsal.toString() : ''}
                onChange={(_ev, nv) => {
                  if ((nv && +nv >= 1 && +nv <= 99) || nv === '') {
                    setDorsal(+nv!);
                    dorsalValido(+nv!);
                    setShowErrors(true);
                  }
                }}
                label="Dorsal (*)"
                // onBlur={() => dorsalValido(dorsal)}
                styles={{
                  fieldGroup: {
                    height: 77,
                    background: !dorsalEsValido && showErrors ? '#ffdbdb' : 'white',
                  },
                  field: { fontSize: 45, textAlign: 'center' },
                }}
              />
              {!dorsalEsValido && showErrors && (
                <span className="error-message">
                  {!!dorsal ? 'Dorsal repetido' : 'Dorsal es obligatorio'}
                </span>
              )}
            </div>

            {isPartidoOnline && (
              <div className="ms-Grid-col ms-sm10">
                <>
                  <Label>Seleccionar Jugador (*)</Label>

                  <JugadorSelector
                    system={system}
                    courier={courier}
                    onSelect={(j) => setJugadorAAgregar(j)}
                    selected={[]}
                    providedList={filterListContext()}
                  ></JugadorSelector>
                  {/* {!jugadorAAgregar && isPartidoOnline && showErrors && (
                    <div className="error-message">No se selecciono un jugador</div>
                  )} */}
                </>
              </div>
            )}
            {!isPartidoOnline && (
              <div className="ms-Grid-col ms-sm10">
                <div className="ms-Grid-col ms-sm6">
                  <InputField
                    input={apellidoInput}
                    onBlur={onBlurExtraCustomInput}
                    label="Apellido (*)"
                  ></InputField>
                </div>
                <div className="ms-Grid-col ms-sm6">
                  <InputField
                    input={nombreInput}
                    onBlur={onBlurExtraCustomInput}
                    label="Nombre (*)"
                  ></InputField>
                </div>
                <div className="ms-Grid-col ms-sm6">
                  <ComboBoxInput
                    input={tipoDocInput}
                    label="Tipo documento (*)"
                    onBlur={onBlurExtraCustomInput}
                    options={tipoDocumentoOptions}
                  />
                </div>
                <div className="ms-Grid-col ms-sm6">
                  <InputField
                    input={nroDocInput}
                    onBlur={onBlurExtraCustomInput}
                    onChangeExtended={yaEstaElDocumento}
                    label="Numero documento (*)"
                  ></InputField>
                </div>
              </div>
            )}
          </div>
          <div className="ms-Grid-col ms-sm11" style={{ paddingLeft: 0, minHeight: 20 }}>
            {yaEstaElDocumento() && (
              <Text variant="mediumPlus" className="error-message" style={{ fontWeight: 600 }}>
                <Icon iconName="AlertSolid" className="error-message text-icon"></Icon>
                Ya fue ingresado un jugador con ese tipo y numero de documento
              </Text>
            )}
          </div>
        </div>
      </DialogTemplate>
    </>
  );
};

export default AgregarJugadorDialog;
